/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


const announcementElement = document.querySelector('.cc-announcement');
const headerElement = document.querySelector('.cc-header');

let totalElementHeight = 0;

if (announcementElement && headerElement) {
  totalElementHeight = announcementElement.offsetHeight + headerElement.offsetHeight;
} else if (announcementElement) {
  totalElementHeight = announcementElement.offsetHeight;
} else if (headerElement) {
  totalElementHeight = headerElement.offsetHeight;
}

const checkScrollPosition = () => {
  if (document.body.classList.contains("product-template")) {
    if (window.scrollY > totalElementHeight) {
      document.body.classList.add('product_stickybar');
    } else {
      document.body.classList.remove('product_stickybar');
    }
  }
};

window.addEventListener('scroll', checkScrollPosition);



document.addEventListener('DOMContentLoaded', () => {
var showmoreTimer = setInterval(function() {
  var checkRecommendationDiv = document.querySelector('product-recommendations');
  if (checkRecommendationDiv && checkRecommendationDiv.textContent.trim().length > 0) {
     document.querySelector('.showmore_wrap').style.display = "block"
  }
  const hiddenItems = document.querySelectorAll('.block-complementary product-recommendations .small__grid li.hidden');
  const totalItems = document.querySelectorAll('.block-complementary product-recommendations .small__grid li').length;
  const remainingCount = document.querySelector('.remaining-count');
  if (totalItems > 0){
    if(remainingCount){
      remainingCount.textContent = `(${hiddenItems.length}+)`;
    }
    const showMoreBtn = document.querySelector('.showmore_btn');
    if(totalItems > 4){
      if(showMoreBtn){
       showMoreBtn.style.display = "block";
      }
    }
    clearInterval(showmoreTimer);
  }
}, 500);
})

function toggleListItems() {
  setTimeout(function(){
  if(document.querySelectorAll('.block-complementary product-recommendations .small__grid li.hidden')){
  const list = document.querySelector('.small__grid');
  list.classList.toggle('show-all');

  const showMoreButton = document.querySelector('.show-more-button');
  const showLessButton = document.querySelector('.show-less-button');

  if (list.classList.contains('show-all')) {
    showMoreButton.style.display = 'none';
    showLessButton.style.display = 'block';
  } else {
    showMoreButton.style.display = 'block';
    showLessButton.style.display = 'none';
  }
  }
 }, 500);
}



  var matchHeight = function(selector) {
    var elements = document.querySelectorAll(selector);
    var maxHeight = 0;
    for (var i = 0; i < elements.length; i++) {
      elements[i].style.height = 'auto';
      if (elements[i].offsetHeight > maxHeight) {
        maxHeight = elements[i].offsetHeight;
      }
    }
    for (var i = 0; i < elements.length; i++) {
      elements[i].style.height = maxHeight + 'px';
    }
  };

  window.onload = function() {
    matchHeight('.card .card__info-inner .card__title');
  };

  window.onresize = function() {
    matchHeight('.card .card__info-inner .card__title');
  };

  matchHeight('.card .card__info-inner .card__title');

document.addEventListener("DOMContentLoaded", function () {
const closeButton = document.querySelector(".close_btn");
    const mobileFixedBar = document.querySelector(".mobile_fixed_bar");
    if (closeButton && mobileFixedBar) {
      closeButton.addEventListener("click", function () {
        mobileFixedBar.classList.remove("open");
      });
    }

  function handleHotspotClick(event) {
      const index = event.currentTarget.getAttribute("data-index");
      showSliderItem(index);
    }

  const hotspotButtons = document.querySelectorAll(".shoppable-image .hotspot");
  hotspotButtons.forEach((button) => {
    button.addEventListener("click", handleHotspotClick);
    button.addEventListener("touchstart", handleHotspotClick);
  });

  function showSliderItem(index) {
   const mobileFixedBar = document.querySelector(".mobile_fixed_bar");
    if (mobileFixedBar) {
    mobileFixedBar.classList.add("open");
    }

    const carousel = document.querySelector('.carousel-slider');
    const targetPosition = index;
    const sliderItems = document.querySelectorAll('.mobile_fixed_bar .relative ul .slider__item');

    let targetSlide;
    sliderItems.forEach(slide => {
    const slidePosition = slide.getAttribute('data-position');

    if (slidePosition === targetPosition.toString()) {
      targetSlide = slide;
    }
    });

    if (targetSlide) {
      const slider = document.querySelector('.mobile_fixed_bar .slider');
      const targetOffset = targetSlide.offsetLeft - slider.offsetLeft;
      slider.scrollTo({ left: targetOffset, behavior: 'smooth' });
    }
  }
});

  function openTab(event, tabName, tabURL) {
    if (window.innerWidth < 769) {
       var LinkTarget = event.target.closest('.cc-featured-collection').querySelector(".black-ot-btn");
    } else {
       var LinkTarget = event.target.closest('.cc-featured-collection').querySelector(".tab_link");
    }
     LinkTarget.setAttribute('href',tabURL);
     const tabContents = event.target.closest('.cc-featured-collection').querySelectorAll(".carousel_content");
    for (const content of tabContents) {
        content.classList.remove("show");
    }
   const tabButtons = event.target.closest('.cc-featured-collection').querySelectorAll(".tab_item");
    for (const button of tabButtons) {
        button.classList.remove("active");
    }
    const selectedTab = document.getElementById(tabName);
    selectedTab.classList.add("show");
    event.currentTarget.classList.add("active");
  }
  var get_len = document.querySelectorAll('.cc-featured-collection');
  get_len.forEach(function(input) {
  const carouselContentstab = input.querySelectorAll('carousel-slider.carousel_content');
    if (carouselContentstab.length > 0) {
    carouselContentstab.forEach((element,index)=>{
    if (index === 0) {
        element.classList.add("show");
    }
    }
  );
  }
});

const tabItems = document.querySelectorAll(".tab_item");
  if (tabItems.length > 0) {
    tabItems[0].classList.add("active");
  }

// product page popup
if (document.body.classList.contains('product-template')) {
 const ProductcloseButton = document.querySelector(".product_feature_mobile .close_btn");
    const ProductFeaturedFixedBar = document.querySelector(".product_feature_mobile");

    if (ProductcloseButton && ProductFeaturedFixedBar) {
      ProductcloseButton.addEventListener("click", function () {
        ProductFeaturedFixedBar.classList.remove("open");
      });
    }

  function handleHotspotClick(event) {
      const index = event.currentTarget.getAttribute("data-index");
      productshowSliderItem(index);
    }

  const ProducthotspotButtons = document.querySelectorAll(".product-features .hotspot");
  ProducthotspotButtons.forEach((button) => {
    button.addEventListener("click", handleHotspotClick);
  });

  function productshowSliderItem(index) {

   const ProductFeaturedFixedBar = document.querySelector(".product_feature_mobile");
    if (ProductFeaturedFixedBar) {
    ProductFeaturedFixedBar.classList.add("open");
    }

    const carousel = document.querySelector('.carousel-slider');
    const targetPosition = index;
    const sliderItems = document.querySelectorAll('.product_feature_mobile .relative ul .slider__item');

    let targetSlide;
    sliderItems.forEach(slide => {
    const slidePosition = slide.getAttribute('data-position');

    if (slidePosition === targetPosition.toString()) {
      targetSlide = slide;
    }
    });

    if (targetSlide) {
      const slider = document.querySelector('.product_feature_mobile .slider');
      const targetOffset = targetSlide.offsetLeft - slider.offsetLeft;
      slider.scrollTo({ left: targetOffset, behavior: 'smooth' });
    }
  }
}
  // video zoom on scroll

const handleIntersection = (entries, observer) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
     const videoScroller = document.querySelector('.video_scroller');
     const nextSection = document.querySelector('.section:nth-of-type(2)');
      window.addEventListener('scroll', function() {
      let scale = (1 + window.scrollY) / 1000;

        if (scale > 1) {
        videoScroller.style.transform = `scale(${scale})`;
        }
          });

    } else {
      console.log('Target div is out of the viewport');
    }
  });
};

const options = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5
};

const observer = new IntersectionObserver(handleIntersection, options);

const targetDiv = document.getElementById('template--18719348850981__70655bac-5d81-48b3-85c4-ec39e7ab72bc');
if(targetDiv){
observer.observe(targetDiv);

}



var timer = setInterval(function() {
  var ratingElements = document.querySelectorAll('.standalone-bottomline');
  var ratingLength = ratingElements.length;

  if (ratingLength > 0) {
    var totalReviewElements = document.querySelectorAll('.standalone-bottomline .yotpo-icon-empty-star');
    var totalReview = totalReviewElements.length;

    if (totalReview < 5) {
      var yotpoBottomLine = document.querySelector('.yotpo.bottomLine');
      yotpoBottomLine.classList.add('show');
    }

    clearInterval(timer);
  }
}, 200);

// Get a reference to the search input element and result items
const searchInput = document.getElementById('header-search');
const resultItems = document.querySelector('.popular-searches');
const resultOverlay = document.querySelector('.js-search-overlay');
const searchMain = document.querySelector('predictive-search');
searchInput.addEventListener('click', function () {
    const searchMainDiv = document.querySelector('.js-search-results .predictive-search');
    resultItems.classList.remove('hide');
    resultItems.classList.add('show');
    resultOverlay.classList.add('is-visible');
    searchMain.setAttribute('open', '');
    if(searchMainDiv){ searchMainDiv.classList.add('hide');}
});

resultOverlay.addEventListener('click', function () {
    resultItems.classList.add('hide');
    resultItems.classList.remove('show');
    resultOverlay.classList.remove('is-visible');
    searchMain.removeAttribute('open');
});

var StickyCartButton = document.querySelector('.card__buttons a');
 if(StickyCartButton){
  StickyCartButton.addEventListener('click', (event) => {
  event.preventDefault();
   window.scrollTo({ top: 0, behavior: 'smooth' });
   return false;
});
 }


document.addEventListener('DOMContentLoaded', function() {
  var VariantInTheBox = document.querySelector('#variant_box');

  if (VariantInTheBox) {
     var closestDisclosure = VariantInTheBox.closest('details-disclosure');
    var hasContent = VariantInTheBox.innerHTML.trim() !== '';
    if (hasContent) {
      closestDisclosure.style.display = "block";
    } else {
      closestDisclosure.style.display = "none";
    }
  } else {
    console.log("No element with the class 'metafield-rich_text_field' found.");
  }
});


document.addEventListener("DOMContentLoaded", function() {

(function(){
if (document.querySelectorAll('.info_list_item-wrap .list_item').length) {
  document.querySelectorAll('.info_list_item-wrap .list_item').forEach(function (ele) {
    ele.addEventListener('click', handleEvent);
    ele.addEventListener('touchstart', handleEvent);
  });
}

function handleEvent(evt) {
  document.querySelectorAll('.info_list_item-wrap .list_item').forEach(function (item) {
    item.classList.remove('active');
  });
    evt.currentTarget.classList.add('active');

    var dataIdToMatch = evt.target.innerHTML;
    var matchingElement = document.querySelector('[data-id="' + dataIdToMatch + '"]');
    var stickyBar = document.querySelector('.product_sticky_info_bar').offsetHeight;

    var ScrollSection = 0;
      if(window.innerWidth < 699){
      offsetDigit = 120
    }else{
      offsetDigit = 100
    }

    if (matchingElement) {
      ScrollSection = matchingElement.offsetTop - stickyBar - offsetDigit ;
    }
    else if (dataIdToMatch === 'Reviews' || dataIdToMatch === 'Review') {
    var ReviewElement = document.querySelector('[data-id="scenario_banner_3"]');
    if (ReviewElement) {
      ScrollSection = ReviewElement.offsetTop - stickyBar - offsetDigit ;
    }
    }

    if (ScrollSection < 0) {
    ScrollSection = 0;
    }

    window.scrollTo({
    top: ScrollSection,
    behavior: 'smooth',
    });


    }
    })()

  function isInViewport(elem) {
    var elementTop = elem.offsetTop;
    var elementBottom = elementTop + elem.offsetHeight / 2;
    var viewportTop = window.scrollY;
    var viewportHalf = viewportTop + window.innerHeight / 2;
    return elementBottom > viewportTop && elementTop < viewportHalf;
  }

  function setActiveLink(getID) {
    var navBarLinks = document.querySelectorAll('.info_list_item-wrap ul li a');
    navBarLinks.forEach(function(link) {
      link.classList.remove('active');
    });
    var navBarItems = document.querySelectorAll('.info_list_item-wrap ul li');
    navBarItems.forEach(function(item) {
      item.classList.remove('active');
    });
    if(getID == 'scenario_banner_3'){
      var activeLink = document.querySelector('.info_list_item-wrap ul li a[data-slidehref="Reviews"]');
    }
    else{
      var activeLink = document.querySelector('.info_list_item-wrap ul li a[data-slidehref="' + getID + '"]');
    }
    if (activeLink) {
      activeLink.classList.add('active');
      activeLink.parentElement.classList.add('active');
    }
  }
  function handleScroll() {
    var scenarioInnerElements = document.querySelectorAll('.scenario_inner');
    scenarioInnerElements.forEach(function(element) {
      if (isInViewport(element)) {
        var getID = element.getAttribute('data-id');
        setActiveLink(getID);
      }
    });
  }

  window.addEventListener('load', handleScroll);
  window.addEventListener('resize', handleScroll);
  window.addEventListener('scroll', handleScroll);

  var linkElements = document.querySelectorAll('.info_list_item-wrap ul li a');
  linkElements.forEach(function(link) {
    link.addEventListener('click', function(e) {
      e.preventDefault();
    });
  });

  

})